import { NICarouselArrow } from '@native-instruments/webex-ni';
import clsx from 'clsx';
import { imageToSrcSet } from 'dotcom-core/src/helpers/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Image } from '~/graphql';

interface CarouselProps {
  images: Array<NonNullable<Image>>;
}

const CarouselNavButton = ({
  onClick,
  disabled,
  isNext = false,
}: {
  onClick: () => void;
  disabled: boolean;
  isNext?: boolean;
}) => {
  return (
    <button
      className={clsx(
        'absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center text-grey-20 hover:text-grey-30 transition-transform duration-300 ease-in-out',
        isNext ? 'right-0 mr-3' : 'left-0 ml-3',
      )}
      aria-label={isNext ? 'Next image' : 'Previous image'}
      onClick={onClick}
      disabled={disabled}
    >
      <NICarouselArrow className={isNext ? 'rotate-180' : ''} />
    </button>
  );
};

const SWIPE_THRESHOLD = 50;

export const Carousel = ({ images }: CarouselProps) => {
  const [index, setIndex] = useState(1); // 1 == first real slide
  const [isAnimating, setIsAnimating] = useState(false);
  const [withTransition, setWithTransition] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // enables loop navigation 1->2->3->1
  const extendedImages = useMemo(() => {
    const first = images[0];
    const last = images[images.length - 1];
    return [last, ...images, first];
  }, [images]);
  const realCount = images.length;
  const hideNavs = realCount < 2;

  const jumpTo = useCallback(
    (target: number) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setWithTransition(true);
      setIndex(target);
    },
    [isAnimating],
  );

  const goNext = useCallback(() => jumpTo(index + 1), [jumpTo, index]);
  const goPrev = useCallback(() => jumpTo(index - 1), [jumpTo, index]);
  const realIndex = (index - 1 + realCount) % realCount;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const changedTouch = e.changedTouches[0];
      if (!changedTouch || touchStartX.current == null) return;
      const deltaX = touchStartX.current - changedTouch.clientX;
      touchStartX.current = null;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
      deltaX > 0 ? goNext() : goPrev();
    },
    [goNext, goPrev],
  );

  const handleTransitionEnd = useCallback(() => {
    setIsAnimating(false);
    if (index === 0) {
      setWithTransition(false); // disable animation for the snap
      setIndex(realCount);
    } else if (index === realCount + 1) {
      setWithTransition(false);
      setIndex(1);
    }
  }, [index, realCount]);

  // keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (hideNavs) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, hideNavs]);

  // Clean possible memory leaks
  useEffect(() => {
    if (!withTransition) {
      animationFrameRef.current = requestAnimationFrame(() =>
        setWithTransition(true),
      );
      return () => {
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };
    }
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [withTransition]);

  return (
    <div
      className={clsx('relative aspect-[720/405]', hideNavs ? 'mb-7' : 'mb-10')}
    >
      <section
        className="relative h-full w-full overflow-hidden rounded-[4px] bg-grey-20"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid="carousel-container"
        aria-roledescription="carousel"
        aria-label="Image carousel"
      >
        <div
          className={clsx(
            'flex h-full',
            withTransition && 'transition-transform duration-300 ease-in-out',
          )}
          style={{ transform: `translateX(-${index * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
          data-testid="carousel-track"
        >
          {extendedImages.map((img, idx) => (
            <div
              key={`${idx}-${img?.uri ?? 'img'}`}
              className="w-full flex-shrink-0"
              aria-roledescription="slide"
              aria-label={`Slide ${idx === 0 ? realCount : idx === extendedImages.length - 1 ? 1 : idx} of ${realCount}`}
            >
              {img && (
                <picture className="h-full w-full">
                  {imageToSrcSet(img).map((src) => (
                    <source
                      key={src?.variationName}
                      media={`(min-width: ${src?.size})`}
                      srcSet={src.srcSet}
                      type="image/webp"
                    />
                  ))}
                  <img
                    className="h-full w-full object-cover"
                    loading="lazy"
                    src={img.uri ?? ''}
                    alt={img.alternativeText || 'Image'}
                    data-testid="product-header-image"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = '';
                    }}
                  />
                </picture>
              )}
            </div>
          ))}
        </div>
      </section>

      {!hideNavs && (
        <div className="hidden lg:block" aria-label="Image carousel navigation">
          <CarouselNavButton onClick={goPrev} disabled={isAnimating} />
          <CarouselNavButton onClick={goNext} disabled={isAnimating} isNext />
        </div>
      )}

      {!hideNavs && (
        <div className="absolute -bottom-4 left-1/2 z-10 flex -translate-x-1/2 space-x-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Go to image ${idx + 1}`}
              onClick={() => jumpTo(idx + 1)}
              disabled={isAnimating}
              className={clsx(
                'h-1.5 w-1.5 rounded-full transition-all duration-300 ease-in-out hover:bg-grey-30',
                idx === realIndex
                  ? 'bg-grey-90 hover:bg-grey-90'
                  : 'bg-grey-20',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const CarouselFallback = () => (
  <div
    data-testid="carousel-placeholder"
    className="bg-grey-20 w-full aspect-[720/405] flex justify-center items-center rounded-[4px]"
  />
);
